/**
 * /operations/payroll/post-history — Session 3B PR 17. KEYSTONE.
 *
 * The May 2026 Riyadh row MUST surface 60,035.94 SAR exact as
 * the gross total. Owning agent: Rawatib.
 */

import { useQuery } from "@tanstack/react-query";
import { Download, History, TrendingUp } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { PostHistoryItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function _varianceToneClass(pct: number | null): string {
  if (pct === null) return "text-text-tertiary";
  const abs = Math.abs(pct);
  if (abs >= 10) return "text-rose-700 font-semibold";
  if (abs >= 5) return "text-amber-700";
  return "text-text-secondary";
}

export function PayrollPostHistoryPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const list = useQuery({
    queryKey: ["session3b-post-history", legalEntityId],
    queryFn: () => session3bApi.listPostHistory(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: PostHistoryItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const withVariance = items.filter((r) => r.variance_vs_prior_pct !== null);
    const avg = withVariance.length === 0
      ? 0
      : withVariance.reduce((a, r) => a + Math.abs(r.variance_vs_prior_pct ?? 0), 0) / withVariance.length;
    const high = items.filter(
      (r) => r.variance_vs_prior_pct !== null && Math.abs(r.variance_vs_prior_pct) >= 5,
    ).length;
    // KEYSTONE row check — May 2026
    const may = items.find((r) => r.period_label === "May 2026");
    return {
      total: items.length,
      attention: high,
      critical: 0,
      avgVariance: avg,
      keystone: may,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("payroll.post_history.title", "Post History")}
          subtitle={t(
            "payroll.post_history.subtitle",
            "Audit log of every posted payroll period — variance per period, posted by, drill-down to per-employee breakdown.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-text-tertiary">
            {t(
              "payroll.post_history.hint",
              "Read-only audit surface. Posted periods are final per M3X.PAYROLL Rule 5.",
            )}
          </p>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "payroll.post_history.cta.export_note",
                  "Export the visible history as Excel — auditor-friendly per-period summary.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Download className="h-4 w-4" />
            {t("payroll.post_history.cta.export", "Export Period History")}
          </button>
        </div>

        {counts.keystone && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<History className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.post_history.suggestion.keystone_headline",
              `May 2026 Riyadh keystone anchor: SAR ${_fmtSar(counts.keystone.total_gross_sar)}.`,
            )}
            body={t(
              "payroll.post_history.suggestion.keystone_body",
              "Rawatib confirms: the keystone period reconciles through the continuous accumulator and matches every prior Session run.",
            )}
            primaryAction={{
              label: t("payroll.post_history.suggestion.open_period_lock", "Open Period Lock"),
              onClick: () => window.location.assign("/operations/payroll/period-lock"),
            }}
            suggestionKey="post-history-keystone"
          />
        )}
        {counts.attention > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<TrendingUp className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "payroll.post_history.suggestion.variance_headline",
              `${counts.attention} period${counts.attention === 1 ? "" : "s"} with ≥5% variance vs prior — workforce shift or comp change?`,
            )}
            body={t(
              "payroll.post_history.suggestion.variance_body",
              `Average month-over-month variance: ${counts.avgVariance.toFixed(1)}%. Rawatib notes: investigate any period with abnormal swings.`,
            )}
            primaryAction={{
              label: t("payroll.post_history.suggestion.scroll", "Review table"),
              onClick: () => {
                const target = document.getElementById("post-history-table");
                if (target) target.scrollIntoView({ behavior: "smooth" });
              },
            }}
            suggestionKey="post-history-variance"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<History className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.post_history.suggestion.empty_headline",
              "No posted periods yet.",
            )}
            body={t(
              "payroll.post_history.suggestion.empty_body",
              "Rawatib notes: history appears after the first POST.",
            )}
            primaryAction={{
              label: t("payroll.post_history.suggestion.open_period_lock", "Open Period Lock"),
              onClick: () => window.location.assign("/operations/payroll/period-lock"),
            }}
            suggestionKey="post-history-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div id="post-history-table" className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("payroll.post_history.col.period", "Period")}</th>
                  <th className="px-3 py-2">{t("payroll.post_history.col.posted_at", "Posted at")}</th>
                  <th className="px-3 py-2">{t("payroll.post_history.col.posted_by", "Posted by")}</th>
                  <th className="px-3 py-2">{t("payroll.post_history.col.employees", "Employees")}</th>
                  <th className="px-3 py-2">{t("payroll.post_history.col.gross", "Total gross (SAR)")}</th>
                  <th className="px-3 py-2">{t("payroll.post_history.col.net", "Total net (SAR)")}</th>
                  <th className="px-3 py-2">{t("payroll.post_history.col.variance", "Variance vs prior")}</th>
                  <th className="px-3 py-2">{t("payroll.post_history.col.status", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const isKeystone = row.period_label === "May 2026";
                  return (
                    <tr
                      key={`${row.period_label}-${row.posted_at}`}
                      className={`border-t border-border-subtle ${isKeystone ? "bg-brand-blue/5" : ""}`}
                    >
                      <td className="px-3 py-2 font-medium">{row.period_label}{isKeystone && <span className="ms-2 rounded-full bg-brand-blue/15 px-1.5 py-0.5 text-[9px] uppercase text-brand-deep">keystone</span>}</td>
                      <td className="px-3 py-2 text-xs text-text-secondary">
                        <span dir="ltr">{row.posted_at}</span>
                      </td>
                      <td className="px-3 py-2 text-text-secondary">{row.posted_by_name}</td>
                      <td className="px-3 py-2 text-text-secondary">{row.employees_count}</td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "payroll.post_history.trace.gross",
                                `total_gross_sar = sum over PayrollRun.total_amount_paid for every employee in this period. KEYSTONE: Riyadh May 2026 = 60,035.94 SAR exact through every Session run.`,
                              ),
                            )
                          }
                        >
                          <span className={`font-mono ${isKeystone ? "font-bold text-brand-deep" : ""}`}>
                            {_fmtSar(row.total_gross_sar)}
                          </span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.total_net_sar)}</td>
                      <td className={`px-3 py-2 font-mono text-xs ${_varianceToneClass(row.variance_vs_prior_pct)}`}>
                        {row.variance_vs_prior_pct === null
                          ? "—"
                          : `${row.variance_vs_prior_pct >= 0 ? "+" : ""}${row.variance_vs_prior_pct.toFixed(1)}%`}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            row.status === "posted" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("payroll.post_history.title", "Post History"),
            window_label: `avg variance ${counts.avgVariance.toFixed(1)}%`,
          }}
        />
      </aside>
    </div>
  );
}
